import React, { useState, useRef, useEffect, useCallback } from 'react';

function CollapsibleSection({ title, icon, defaultExpanded = true, children }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const bodyRef = useRef(null);
  const pendingEndRef = useRef(null);
  const pendingFrameRef = useRef(null);

  const clearPendingEnd = useCallback(() => {
    const body = bodyRef.current;
    if (body && pendingEndRef.current) {
      body.removeEventListener('transitionend', pendingEndRef.current);
    }
    pendingEndRef.current = null;
    if (pendingFrameRef.current != null) {
      cancelAnimationFrame(pendingFrameRef.current);
      pendingFrameRef.current = null;
    }
  }, []);

  const toggle = useCallback(() => {
    const body = bodyRef.current;
    if (!body) return;

    clearPendingEnd();

    if (expanded) {
      body.style.overflow = 'hidden';
      body.style.maxHeight = body.scrollHeight + 'px';
      pendingFrameRef.current = requestAnimationFrame(() => {
        pendingFrameRef.current = null;
        body.classList.add('collapsed');
      });
    } else {
      body.classList.remove('collapsed');
      body.style.maxHeight = body.scrollHeight + 'px';
      const onEnd = (event) => {
        if (event && event.propertyName !== 'max-height') return;
        clearPendingEnd();
        if (body.classList.contains('collapsed')) return;
        body.style.maxHeight = '';
        body.style.overflow = 'visible';
      };
      pendingEndRef.current = onEnd;
      body.addEventListener('transitionend', onEnd);
    }

    setExpanded(!expanded);
  }, [expanded, clearPendingEnd]);

  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    if (!defaultExpanded) {
      body.classList.add('collapsed');
      body.style.maxHeight = '0px';
    } else {
      body.style.overflow = 'visible';
    }
  }, []);

  return (
    <div className="cfm-section">
      <div
        className="cfm-section-header"
        role="button"
        aria-expanded={String(expanded)}
        onClick={toggle}
      >
        {icon && (
          <svg
            className="cfm-section-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            dangerouslySetInnerHTML={{ __html: icon }}
          />
        )}
        <span className="cfm-section-title">{title}</span>
        <span className="cfm-section-chevron">&#9662;</span>
      </div>
      <div className="cfm-section-body" ref={bodyRef}>
        {children}
      </div>
    </div>
  );
}

export default CollapsibleSection;
